/**
 * Layout for a single foundation run: header (Back to Foundations) + sub-nav (Target | TFD | ALO | Foundational Symbols) + outlet.
 * Used at /dashboard/brand-strategy/foundations/:runId. Sub-nav shows when you click into a run from the Foundations dashboard.
 */

import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, Outlet, useLocation } from 'react-router-dom'
import { FileText, Loader2 } from 'lucide-react'
import { ROUTES } from '@/constants/routes'
import { Button } from '@/components/ui/button'
import { CampaignPageHeader } from '@/modules/campaigns/components'
import { EditableFoundationBaselineName } from './EditableFoundationBaselineName'
import { FoundationsSubNav } from './FoundationsSubNav'
import { SubjectStackCrossLinks, type SubjectStackType } from '@/modules/campaigns/components/SubjectStackCrossLinks'
import { FoundationPasteImportModal } from './modals/FoundationPasteImportModal'
import { FoundationPasteImportBanner } from './FoundationPasteImportBanner'
import { FoundationPasteImportProvider, useFoundationPasteImport } from '../context/FoundationPasteImportContext'
import { persuasionLabService } from '@/services/persuasion-lab'
import { useAuthContext } from '@/context'
import { ChannelProvider } from 'ably/react'
import {
    foundationsStepFromPath,
    setFoundationsRecent,
} from '../utils/foundations-recent-context'

const PasteImportHeaderButton: FC = () => {
    const { openModal, isImportActive } = useFoundationPasteImport()

    return (
        <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={() => openModal(isImportActive ? 'progress' : 'guide')}
            className='border-cz-purple-700 text-cz-purple-700'
        >
            {isImportActive ? (
                <Loader2 className='w-4 h-4 mr-2 animate-spin' />
            ) : (
                <FileText className='w-4 h-4 mr-2' />
            )}
            {isImportActive ? 'View import progress' : 'Build from pasted text'}
        </Button>
    )
}

const FoundationsRunLayoutBody: FC<{
    refetchRunRef: React.MutableRefObject<() => void>
}> = ({ refetchRunRef }) => {
    const { runId } = useParams<{ runId: string }>()
    const navigate = useNavigate()
    const location = useLocation()
    const { orgAccount } = useAuthContext()
    const [runName, setRunName] = useState<string>('')
    const [run, setRun] = useState<any>(null)
    const { syncFromRunMetadata } = useFoundationPasteImport()

    const refetchRun = useCallback(() => {
        if (!runId) return
        persuasionLabService
            .getRun(runId)
            .then((data: any) => {
                setRun(data || null)
                setRunName(data?.name || `Foundation run`)
                syncFromRunMetadata(data)
            })
            .catch(() => navigate(ROUTES.PERSUASION_LAB_FOUNDATIONS, { replace: true }))
    }, [runId, navigate, syncFromRunMetadata])

    useEffect(() => {
        refetchRunRef.current = refetchRun
    }, [refetchRun, refetchRunRef])

    useEffect(() => {
        refetchRun()
    }, [refetchRun])

    useEffect(() => {
        if (!runId || !run) return
        const { stepId, stepLabel } = foundationsStepFromPath(location.pathname, runId)
        setFoundationsRecent(orgAccount?.org?.id, {
            runId,
            runName: run.name || runName || 'Brand baseline',
            subjectName: (run as any).cpp_anchor_name,
            anchorType: run.cpp_anchor_type,
            stepId,
            stepLabel,
            pathname: location.pathname,
        })
    }, [runId, run, runName, location.pathname, orgAccount?.org?.id])

    const headerSubtitle = (() => {
        const subject = (run as any)?.cpp_anchor_name?.trim()
        const baseline = runName?.trim()
        if (subject && baseline && subject.toLowerCase() !== baseline.toLowerCase()) {
            return `${subject} · ${baseline}`
        }
        return subject || baseline || 'Brand baseline'
    })()

    const anchorType = run?.cpp_anchor_type as SubjectStackType | undefined
    const anchorId = run?.cpp_anchor_id ? String(run.cpp_anchor_id) : undefined
    const showSubjectCrossLinks =
        Boolean(runId && anchorType && anchorId && ['person', 'company', 'product'].includes(anchorType))

    const headerTitle =
        run && runId ? (
            <EditableFoundationBaselineName
                variant='header'
                run={{ ...run, _id: runId, name: runName || run.name }}
                onRenamed={(_id, name) => {
                    setRunName(name)
                    setRun((prev: any) => (prev ? { ...prev, name } : prev))
                }}
            />
        ) : (
            'Loading…'
        )

    return (
        <div className='min-h-screen bg-background'>
            {/* Single sticky stack: avoids sub-nav offset jumping over the paste-import banner */}
            <div className='sticky top-0 z-30'>
                <CampaignPageHeader
                    title={headerTitle}
                    subtitle={headerSubtitle}
                    onBack={() => navigate(ROUTES.PERSUASION_LAB_FOUNDATIONS)}
                    showContinue={false}
                    sticky={false}
                >
                    {runId ? <PasteImportHeaderButton /> : null}
                </CampaignPageHeader>
                {showSubjectCrossLinks && anchorType && anchorId && runId ? (
                    <div className='border-b border-slate-200 bg-slate-50/90 px-4 sm:px-6 py-2'>
                        <SubjectStackCrossLinks
                            subjectType={anchorType}
                            subjectId={anchorId}
                            activeSurface='foundation'
                            foundationRunId={runId}
                            compact
                        />
                    </div>
                ) : null}
                <FoundationsSubNav sticky={false} />
            </div>
            <FoundationPasteImportBanner />
            <main className='w-full min-w-0 max-w-7xl mx-auto py-4 sm:py-6 lg:py-8 px-4 sm:px-6 overflow-x-hidden'>
                <Outlet />
            </main>
            {runId && (
                <FoundationPasteImportModal runId={runId} run={run} onComplete={refetchRun} />
            )}
        </div>
    )
}

export const FoundationsRunLayout: FC = () => {
    const { runId } = useParams<{ runId: string }>()
    const refetchRunRef = useRef<() => void>(() => {})

    return (
        <ChannelProvider channelName={`run:${runId || 'none'}`}>
            <FoundationPasteImportProvider
                runId={runId || ''}
                onImportComplete={() => refetchRunRef.current()}
            >
                <FoundationsRunLayoutBody refetchRunRef={refetchRunRef} />
            </FoundationPasteImportProvider>
        </ChannelProvider>
    )
}
